package com.siriusxm.pia

import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonObject

@Serializable
public data class Configuration(
    val stage: String,
    val authUrl: String,
    val clientId: String,
    val apiUrl: String,
    val transcriptionApiUrl: String,
    val transcriptionEditorBaseUrl: String?,
    val unifiedContentBusApiUrl: String,
    val atlasImageServerBaseUrl: String,
    val artistStationApiUrl: String,
    val artistStationTracksApiUrl: String? = null,
    val mddbApiUrl: String,
    val unifiedAggregatorApiUrl: String,
    val epgApiUrl: String,
    // Cross-environment episode comparison (UCA-1098): each deployed stage's config carries the
    // *other* two stages' EPG API base URLs under these keys (see epg/configs/{dev,test,prod}.json).
    // Without them here, ApplicationContainer.configProperty -- which reads off this typed
    // Configuration rather than the raw config.json -- silently returns null for all three, since
    // ignoreUnknownKeys drops anything this class does not declare during the decode in
    // Application.config(). That makes every comparison target unavailable with no error anywhere.
    val epgApiUrl_dev: String? = null,
    val epgApiUrl_test: String? = null,
    val epgApiUrl_prod: String? = null,
    val musicApiUrl: String? = null,
    val ingestionAuditUrl: String,
    val datadogLookupAtlasBusById: String,
    val sportsApiUrl: String? = null,
    val imageFactoryApiUrl: String? = null,
    val applications: List<ApplicationDescriptor>? = null,
    val datadogLookupByJobId: String,
    val pkceEnabled: String,
    val sportsReplayEnabled: String,
    val sxmPlayerBaseUrl: String? = null,
    val cmcApiUrl: String? = null,
    val sxmPlaybackEnabled: Boolean = false,
    val transcriptionDashboardBaseUrl: String? = null,
    // Base URL of the shared unified-content AVP `/auth-check` API (UCA-1091). The browser calls it
    // through cmc-core's Capabilities to discover which named actions the current user's token is
    // allowed. When absent, capability checks fail closed (every action is treated as denied), so
    // gated affordances stay hidden. Permissions are granted identically across environments, so the
    // same (prod) auth-check URL is used for every stage.
    val authCheckApiUrl: String? = null,
    // Base URL of the Unified Content Permissions (UCP / service registry) API (UCA-1212). The Team
    // section on producers and consumers reads the linked team and its members from it with the
    // viewer's token. Permissions are managed in production UCP, so the deployed dev, test and prod
    // configs all use the prod URL, as authCheckApiUrl does. Only the local-dev configs (the default
    // src/jsMain/resources/config.json and configs/jnichols.json) use the dev UCP API, because their
    // dev Cognito tokens are only accepted there.
    // When absent, the Team section shows that team details are unavailable.
    val ucpApiUrl: String? = null,
    // Base URL of the production permissions app (UCA-1212), for links from the Team box to team and
    // member pages. Permissions are managed in production for every environment, and dev and test do
    // not mount the app, so links always open the production console, in a separate tab.
    val permissionsBaseUrl: String = "https://content-management.siriusxm.com/#permissions"
)

@Serializable
public data class ApplicationDescriptor(
    val id: String? = null,
    val name: String,
    val description: String? = null,
    val path: String,
    val requiredGroup: List<String>? = null,
    // UCA-1181: id of a unified-content (UCP / AVP) action, e.g. "MountainServices_ViewDashboard",
    // that also grants access to this application, in addition to members of requiredGroup.
    // Resolved through Capabilities (UCA-1091); hide-only, the application's own services enforce it.
    val allowedCapability: String? = null,
    val initOnStartup: Boolean = false,
    // A single "Open in..." link for an entity; shorthand for one [entityLinks] entry that uses this
    // application's name and [types]. Ignored when [entityLinks] is set.
    val entityPath: String? = null,
    // UCA-1196: every "Open in..." link this application offers, e.g. a cut's cutMarker and
    // segmentMarker pages. Takes precedence over [entityPath].
    val entityLinks: List<EntityLink>? = null,
    val image: String? = null,
    val source: String? = null,
    val plugins: Map<String, JsonObject>? = null,
    // if no types are specified, all types are allowed
    val types: List<String>? = null
) {
    public fun internal(): Boolean {
        return !this.path.startsWith("http")
    }

    /**
     * The "Open in..." links this application offers: [entityLinks] when set, otherwise the single
     * [entityPath] link, otherwise none.
     */
    public fun openInLinks(): List<EntityLink> =
        entityLinks ?: entityPath?.let { listOf(EntityLink(path = it, types = types)) }.orEmpty()
}

/**
 * One "Open in..." link an application offers for an entity (UCA-1196).
 *
 * @param name the menu label; null uses the application's name
 * @param path the link, with placeholders such as `{{ATLAS_ID}}`, `{{SOURCE_ID}}` or `{{CUT_GUID}}`,
 *        or an entity field by path such as `{{data.reportingId}}` or `{{atlas.cutId|guid}}` (UCA-1277;
 *        see `buildApplicationLinkUrl` for the full syntax).
 *        Relative to the application's own `path` when the application is hosted in the console.
 * @param types the entity types the link applies to; null applies it to every type
 * @param image the menu icon (UCA-1277); null uses the application's image
 */
@Serializable
public data class EntityLink(
    val name: String? = null,
    val path: String,
    val types: List<String>? = null,
    val image: String? = null,
)